import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Select, Table, Tag, Tooltip, message, Popconfirm, Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Consignment, ConsignmentItem, ConsignmentPeriod, ConsignmentStatus, ConsignmentClosePeriodResult, ConsignmentAddStockBulkPayload, ConsignmentAddStockBulkResult, ConsignmentStockCheckPreviewRequest, ConsignmentStockCheckPreviewResult, ConsignmentEditBatchPayload, ConsignmentVoidBatchPayload } from '../../model/consignment';
import { api_consignment } from '../../api/consignment';
import ConsignmentModal from './consignment-modal';
import ConsignmentCheckModal from './consignment-check-modal';
import ConsignmentSettleModal from './consignment-settle-modal';
import ConsignmentHistoryModal from './consignment-history-modal';
import ConsignmentAddStockModal from './consignment-add-stock-modal';
import ConsignmentReturnModal, { ReturnToOwnerPayload } from './consignment-return-modal';
import ConsignmentClosePeriodModal, { ClosePeriodPayload } from './consignment-close-period-modal';
import ConsignmentPeriodHistoryModal from './consignment-period-history-modal';
import ConsignmentNewPeriodAddStockModal, { NewPeriodAddStockContext } from './consignment-new-period-add-stock-modal';
import ConsignmentBatchEditModal from './consignment-batch-edit-modal';
import ConsignmentBatchVoidModal from './consignment-batch-void-modal';
import ConsignmentCountProfitModal from './consignment-count-profit-modal';
import { useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';
import '../../assets/css/consignment.css';
import { deleteFailureMessage } from '../../utils/write-result';

const sourceTypeLabel = (sourceType?: string) => {
    switch (sourceType) {
        case 'INITIAL': return 'ฝากเริ่มต้น';
        case 'ADD_STOCK': return 'ฝากเพิ่ม';
        case 'CARRY_FORWARD': return 'ยกยอดจากรอบก่อน';
        default: return sourceType || '-';
    }
};

const isVoidedItem = (item: ConsignmentItem) => (item.item_status || 'ACTIVE') === 'VOIDED';

/** Why "แก้ไข" / "ลบ/ยกเลิก" is greyed out for this lot, or null when it
 * is allowed. Backend's own guard answers per lot in GET
 * (`edit_block_reason`); Backend checks again on save, so this only decides
 * what the buttons show. No answer at all means a Backend without these
 * operations, and the buttons stay off. */
export const batchChangeBlockReason = (item: ConsignmentItem): string | null => {
    if (item.edit_block_reason === undefined) return 'ระบบหลังบ้านยังไม่รองรับการแก้ไขล็อต';
    return item.edit_block_reason;
};

/** "รายการล็อตทั้งหมด": every lot of the bill, the cancelled ones too (shown
 * greyed out). GET keeps those apart in `voided_items` so nothing that sums
 * `items` can count them. */
export const buildLotRows = (record: Consignment): ConsignmentItem[] =>
    sortBatchesNewestFirst([...(record.items || []), ...(record.voided_items || [])], record.periods, record.current_period);

/** UPDATE_REMAINING body for "นับ": the counted products only, the note,
 * and the Period the stock-check modal observed -- so a Period closed while
 * the modal was open is refused (STALE_PERIOD / ALREADY_CLOSED) instead of
 * the count landing in the next one. */
export const buildStockCheckSaveBody = (
    countItems: { product_id: number; latest_actual_remaining: number }[],
    note?: string,
    expectedPeriodId?: number
) => ({
    ...(expectedPeriodId !== undefined && expectedPeriodId !== null ? { expected_period_id: expectedPeriodId } : {}),
    items: countItems,
    note,
});

/** What "+ เพิ่มสินค้าใหม่ในรอบนี้" opens with, from a fresh GET -- or null
 * when the bill has no open Period (nothing to add to). Products with an
 * ACTIVE lot in the open Period are listed so the picker can send those to
 * their own "+ ฝากเพิ่ม" instead. */
export const buildAddNewProductsContext = (round: Consignment, full: Consignment | null | undefined): NewPeriodAddStockContext | null => {
    const current = full?.current_period;
    if (!full || !current) return null;
    const activeProductIds = Array.from(new Set((full.items || [])
        .filter(it => it.period_id === current.id && (it.item_status || 'ACTIVE') === 'ACTIVE')
        .map(it => it.product_id)));
    return {
        consignmentId: round.id,
        documentNo: round.document_no,
        vendorName: round.vendor_name,
        newPeriod: { id: current.id, period_no: current.period_no },
        carryForwardItems: [],
        mode: 'ADD_NEW_PRODUCTS',
        activeProductIds
    };
};

interface BatchActionsProps {
    item: ConsignmentItem;
    detail: React.ReactNode;
    onEdit: () => void;
    onVoid: () => void;
}

/** The "รายละเอียด" cell: 👁 always; ✏ and 🗑 on every lot that is not
 * already cancelled -- greyed out, with the reason, when Backend says the
 * lot can no longer be fixed. */
export const ConsignmentBatchActions: React.FC<BatchActionsProps> = ({ item, detail, onEdit, onVoid }) => {
    const blocked = batchChangeBlockReason(item);
    return (
        <div className="consignment-batch-actions" data-testid={`batch-actions-${item.id}`}>
            <Popover
                content={detail}
                title="รายละเอียดเพิ่มเติม"
                trigger="click"
                overlayClassName="consignment-batch-detail-popover-overlay"
            >
                <button
                    type="button"
                    className="consignment-btn-detail"
                    data-testid={`btn-batch-detail-${item.id}`}
                    aria-label="ดูรายละเอียด"
                    title="ดูรายละเอียด"
                >
                    <FontAwesomeIcon icon="eye" />
                </button>
            </Popover>
            {!isVoidedItem(item) && (
                <>
                    <Tooltip title={blocked ? `แก้ไขไม่ได้: ${blocked}` : 'แก้ไขล็อต'}>
                        <span className="consignment-batch-action-wrap" data-testid={`btn-batch-edit-wrap-${item.id}`}>
                            <button
                                type="button"
                                className="consignment-btn-detail edit"
                                data-testid={`btn-batch-edit-${item.id}`}
                                aria-label="แก้ไขล็อต"
                                disabled={!!blocked}
                                onClick={onEdit}
                            >
                                <FontAwesomeIcon icon="pen-to-square" />
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip title={blocked ? `ลบ/ยกเลิกไม่ได้: ${blocked}` : 'ลบ/ยกเลิกรายการ'}>
                        <span className="consignment-batch-action-wrap" data-testid={`btn-batch-void-wrap-${item.id}`}>
                            <button
                                type="button"
                                className="consignment-btn-detail void"
                                data-testid={`btn-batch-void-${item.id}`}
                                aria-label="ลบ/ยกเลิกรายการ"
                                disabled={!!blocked}
                                onClick={onVoid}
                            >
                                <FontAwesomeIcon icon="trash-can" />
                            </button>
                        </span>
                    </Tooltip>
                </>
            )}
        </div>
    );
};

/** "ที่มา" in the lot table; a cancelled lot says so. */
export const ConsignmentLotSource: React.FC<{ item: ConsignmentItem }> = ({ item }) => (
    <span className="consignment-cell-sub">
        {sourceTypeLabel(item.source_type)}
        {isVoidedItem(item) && (
            <Tag className="consignment-tag-voided" data-testid={`tag-voided-${item.id}`} style={{ marginLeft: 6 }}>
                ยกเลิกแล้ว
            </Tag>
        )}
    </span>
);

interface LotManageProps {
    roundStatus: ConsignmentStatus;
    item: ConsignmentItem;
    onReturn: () => void;
    onCloseItem: () => void;
}

/** "จัดการสินค้า": the lot's own business events -- return goods to the
 * owner, close the lot. Separate from fixing a wrong entry (แก้ไข / ยกเลิก),
 * which lives in "รายละเอียด". */
export const ConsignmentLotManageActions: React.FC<LotManageProps> = ({ roundStatus, item, onReturn, onCloseItem }) => {
    if (isVoidedItem(item)) return <span className="consignment-cell-sub">ยกเลิกแล้ว</span>;
    const itemActive = roundStatus === 'ACTIVE' && (item.item_status || 'ACTIVE') === 'ACTIVE';
    if (!itemActive) return <span className="consignment-cell-sub">ปิดแล้ว</span>;
    // "ฝากเพิ่ม" is deliberately NOT repeated here per Batch row
    // anymore -- every deposit creates a brand-new Batch
    // regardless of which existing Batch row you'd click from,
    // so one product-level action in the "สรุปตามสินค้า"
    // summary above replaces what used to be N duplicated
    // buttons for the same Product. "คืนผู้ฝาก" and "ปิดรายการ
    // สินค้า" remain here because they ARE genuinely
    // Batch/item-level actions (you return or close ONE
    // specific Batch, never "a Product" as a whole).
    return (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            {Number(item.latest_actual_remaining || 0) > 0 && (
                <Button
                    size="small"
                    danger
                    className="consignment-v15-action consignment-v15-action-return"
                    onClick={onReturn}
                    data-testid={`btn-return-${item.id}`}
                >
                    คืนผู้ฝาก
                </Button>
            )}
            <Popconfirm
                overlayClassName="consignment-v15-popconfirm"
                title="ปิดรายการสินค้านี้?"
                description="ปิดได้เมื่อคงเหลือ 0 และไม่มียอดค้างจ่ายเท่านั้น"
                onConfirm={onCloseItem}
                okText="ปิดรายการสินค้า"
                cancelText="ยกเลิก"
            >
                <Button
                    size="small"
                    className="consignment-v15-action consignment-v15-action-close"
                    data-testid={`btn-close-item-${item.id}`}
                >
                    ปิดรายการสินค้า
                </Button>
            </Popconfirm>
        </div>
    );
};

interface PeriodBannerProps {
    record: Consignment;
    onAddNewProducts: () => void;
    onOpenPeriodHistory: () => void;
}

/** The bill's current-Period line, with what can be done to that Period. */
export const ConsignmentPeriodBanner: React.FC<PeriodBannerProps> = ({ record, onAddNewProducts, onOpenPeriodHistory }) => {
    const currentPeriod = record.current_period;
    return (
        <div className="consignment-period-banner" data-testid={`period-banner-${record.id}`}>
            <div>
                {currentPeriod ? (
                    <span>
                        <strong>รอบบิลที่ {currentPeriod.period_no}</strong>
                        {' '}<Tag color="green">เปิดอยู่</Tag>
                        <span className="consignment-period-banner-meta">เปิดเมื่อ {currentPeriod.opened_at || '-'}</span>
                    </span>
                ) : (
                    <span>
                        <strong>ไม่มีรอบบิลที่เปิดอยู่</strong>
                        {record.status === 'CLOSED' && <Tag style={{ marginLeft: 8 }}>ปิดรายการฝากขายทั้งหมดแล้ว</Tag>}
                    </span>
                )}
            </div>
            <div className="consignment-period-banner-actions">
                {record.status === 'ACTIVE' && currentPeriod && (
                    <Button
                        size="small"
                        className="consignment-btn-add-new-product"
                        onClick={onAddNewProducts}
                        data-testid={`btn-add-new-product-${record.id}`}
                    >
                        + เพิ่มสินค้าใหม่ในรอบนี้
                    </Button>
                )}
                <Button
                    size="small"
                    className="consignment-btn-period-history"
                    onClick={onOpenPeriodHistory}
                    data-testid={`btn-period-history-${record.id}`}
                >
                    🗂️ ดูรอบบิลย้อนหลัง{record.period_count ? ` (${record.period_count})` : ''}
                </Button>
            </div>
        </div>
    );
};

// Newest-relevant-first ordering for the Batch table: rows belonging to
// the OPEN Period surface before any CLOSED Period, and within a Period
// the most recently created Batch comes first. Without this, a Batch
// created right after closing a Period (carry-forward, or a fresh
// ADD_STOCK) sinks to the bottom below every older, already-settled row --
// exactly the "can't find the new batch" complaint this fixes. Display-
// only ordering; never mutates the source array or any persisted data.
export const sortBatchesNewestFirst = (
    items: ConsignmentItem[],
    periods?: ConsignmentPeriod[],
    currentPeriod?: ConsignmentPeriod | null
): ConsignmentItem[] => {
    const periodNoById = new Map((periods || []).map(p => [p.id, p.period_no]));
    return [...items].sort((a, b) => {
        const aOpen = currentPeriod && a.period_id === currentPeriod.id ? 1 : 0;
        const bOpen = currentPeriod && b.period_id === currentPeriod.id ? 1 : 0;
        if (aOpen !== bOpen) return bOpen - aOpen;

        const aPeriodNo = periodNoById.get(a.period_id as number) ?? 0;
        const bPeriodNo = periodNoById.get(b.period_id as number) ?? 0;
        if (aPeriodNo !== bPeriodNo) return bPeriodNo - aPeriodNo;

        const aBatchNo = Number(a.batch_no || 0);
        const bBatchNo = Number(b.batch_no || 0);
        if (aBatchNo !== bBatchNo) return bBatchNo - aBatchNo;

        return Number(b.id || 0) - Number(a.id || 0);
    });
};

export interface ConsignmentProductSummaryRow {
    product_id: number;
    product_name?: string;
    product_unit_name?: string;
    received: number;
    remaining: number;
    sold: number;
    settled: number;
    batchCount: number;
    /** Most-recent ACTIVE Batch of this Product in the given Period -- used
     * only as the single product-level "ฝากเพิ่ม" action's reference item
     * (its price/cost pre-fill the add-stock form). Every deposit still
     * creates a brand-new Batch regardless of which Batch this points at
     * (locked V2 rule). undefined when no Batch of this Product is ACTIVE. */
    referenceBatch?: ConsignmentItem;
}

// One row per Product for the "สรุปตามสินค้า" summary -- also the single
// source of which Batch backs the one product-level "ฝากเพิ่ม" action,
// replacing what used to be a repeated "ฝากเพิ่ม" button on every Batch
// row of the same Product (V2 correctly keeps Carry Forward and ADD_STOCK
// as separate Batch rows, which used to mean separate buttons too).
// Exported as a pure function (no hooks) so it's unit-testable without
// needing to render AntD's Table.expandable, which real browsers render
// fine but whose fixed-column + expandable combination does not reliably
// materialize expandedRowRender's output in jsdom.
export const buildProductSummary = (
    items: ConsignmentItem[],
    currentPeriod?: ConsignmentPeriod | null
): ConsignmentProductSummaryRow[] => {
    const relevantBatches = (items || []).filter(it => (!currentPeriod || it.period_id === currentPeriod.id) && !isVoidedItem(it));
    const map = new Map<number, ConsignmentProductSummaryRow>();
    relevantBatches.forEach(it => {
        const existing = map.get(it.product_id) || {
            product_id: it.product_id,
            product_name: it.product_name,
            product_unit_name: it.product_unit_name,
            received: 0, remaining: 0, sold: 0, settled: 0, batchCount: 0,
            referenceBatch: undefined as ConsignmentItem | undefined
        };
        existing.received += Number(it.quantity_received || 0);
        existing.remaining += Number(it.latest_actual_remaining || 0);
        existing.sold += Number(it.quantity_sold || 0);
        existing.settled += Number(it.quantity_settled || 0);
        existing.batchCount += 1;
        if ((it.item_status || 'ACTIVE') === 'ACTIVE') {
            if (!existing.referenceBatch || Number(it.batch_no || 0) > Number(existing.referenceBatch.batch_no || 0)) {
                existing.referenceBatch = it;
            }
        }
        map.set(it.product_id, existing);
    });
    return Array.from(map.values());
};

interface AddStockActionProps {
    roundStatus: ConsignmentStatus;
    item: ConsignmentItem;
    onClick: () => void;
}

export const ConsignmentAddStockAction: React.FC<AddStockActionProps> = ({ roundStatus, item, onClick }) => {
    if (roundStatus !== 'ACTIVE' || (item.item_status || 'ACTIVE') !== 'ACTIVE') {
        return <span className="consignment-cell-sub">ปิดแล้ว</span>;
    }
    return (
        <Tooltip title="ฝากเพิ่ม">
            <button
                type="button"
                className="consignment-btn-add-stock"
                data-testid={`btn-add-stock-${item.id}`}
                aria-label="ฝากเพิ่ม"
                title="ฝากเพิ่ม"
                onClick={onClick}
            >
                <FontAwesomeIcon icon="plus" />
                <span>ฝากเพิ่ม</span>
            </button>
        </Tooltip>
    );
};

export const resolveAddStockRequestContext = (
    round: Consignment | null,
    item: ConsignmentItem | null,
    payload: any
) => {
    if (!round) throw new Error('ไม่พบรอบการฝากขาย');
    if (!item) throw new Error('ไม่พบรายการสินค้าที่ต้องการฝากเพิ่ม');
    const roundId = round.id;
    if (!Number.isInteger(roundId) || roundId <= 0) {
        throw new Error('รหัสรอบการฝากขายไม่ถูกต้อง');
    }
    if (item.consignment_id !== undefined && item.consignment_id !== roundId) {
        throw new Error('รอบการฝากขายของสินค้าไม่ตรงกับรอบที่เลือก กรุณาโหลดข้อมูลใหม่');
    }
    if (payload?.consignment_item_id !== item.id) {
        throw new Error('รหัสรายการสินค้าที่ส่งไม่ตรงกับรายการที่เลือก');
    }
    return { roundId, payload };
};

export const ConsignmentPage: React.FC = () => {
    // `pageSize: 10` was a literal, so antd read it as a controlled value and
    // there was no size changer to move it anyway.
    const pagination = useTablePagination();
    const { ref: tableRef, y: scrollY } = useMeasuredRowScrollY(380);
    const [consignments, setConsignments] = useState<Consignment[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('ALL');

    // Modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<Consignment | null>(null);

    const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
    const [checkingItem, setCheckingItem] = useState<Consignment | null>(null);

    const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);
    const [settlingItem, setSettlingItem] = useState<Consignment | null>(null);

    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [historyItem, setHistoryItem] = useState<Consignment | null>(null);
    const [addStockRound, setAddStockRound] = useState<Consignment | null>(null);
    const [addStockItem, setAddStockItem] = useState<ConsignmentItem | null>(null);
    const [returnRound, setReturnRound] = useState<Consignment | null>(null);
    const [returnItem, setReturnItem] = useState<ConsignmentItem | null>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const [isClosePeriodModalOpen, setIsClosePeriodModalOpen] = useState(false);
    const [closePeriodRound, setClosePeriodRound] = useState<Consignment | null>(null);
    const [isPeriodHistoryModalOpen, setIsPeriodHistoryModalOpen] = useState(false);
    const [periodHistoryItem, setPeriodHistoryItem] = useState<Consignment | null>(null);

    const [isNewPeriodAddStockModalOpen, setIsNewPeriodAddStockModalOpen] = useState(false);
    const [newPeriodAddStockContext, setNewPeriodAddStockContext] = useState<NewPeriodAddStockContext | null>(null);

    // The lot being fixed ("แก้ไข") or cancelled ("ลบ/ยกเลิก"), with its bill.
    const [editBatchTarget, setEditBatchTarget] = useState<{ round: Consignment; item: ConsignmentItem } | null>(null);
    const [voidBatchTarget, setVoidBatchTarget] = useState<{ round: Consignment; item: ConsignmentItem } | null>(null);

    // กำไรจากการตรวจนับ: read-only report across every bill.
    const [isCountProfitOpen, setIsCountProfitOpen] = useState(false);

    useEffect(() => {
        loadConsignments();
    }, [statusFilter]);

    const loadConsignments = async (preserveExpandedKeys?: React.Key[]) => {
        setLoading(true);
        try {
            const res = await api_consignment('LIST', {
                status: statusFilter !== 'ALL' ? statusFilter : undefined,
                search: searchQuery.trim() || undefined
            });
            const list: Consignment[] = Array.isArray(res) ? res : [];
            const targetExpandedKeys = Array.isArray(preserveExpandedKeys) ? preserveExpandedKeys : expandedRowKeys;

            if (targetExpandedKeys.length > 0) {
                const detailedItems = await Promise.all(
                    targetExpandedKeys.map(async (key) => {
                        try {
                            const full = await api_consignment('GET', {}, Number(key));
                            return { id: Number(key), full };
                        } catch {
                            return { id: Number(key), full: null };
                        }
                    })
                );
                const detailMap = new Map(detailedItems.filter(d => d.full).map(d => [d.id, d.full]));
                setConsignments(list.map(c => detailMap.has(c.id) ? { ...c, ...detailMap.get(c.id) } : c));
            } else {
                setConsignments(list);
            }
        } catch (e) {
            console.error('Failed to load consignments', e);
            message.error('โหลดข้อมูลสินค้าฝากขายไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadConsignments();
    };

    // Summary Statistics
    const stats = useMemo(() => {
        let totalRounds = consignments.length;
        let totalReceived = 0;
        let totalSold = 0;
        let totalSales = 0;
        let totalPayable = 0;
        let totalProfit = 0;

        consignments.forEach(item => {
            totalReceived += Number(item.total_received_qty || 0);
            totalSold += Number(item.total_sold_qty || 0);
            totalSales += Number(item.total_sales_amount || 0);
            totalPayable += Number(item.total_unpaid_owner_payable ?? item.total_owner_payable ?? 0);
            totalProfit += Number(item.total_shop_profit || 0);
        });

        return {
            totalRounds,
            totalReceived,
            totalSold,
            totalSales,
            totalPayable,
            totalProfit
        };
    }, [consignments]);

    // Open Edit: fetch single consignment details with items
    const handleOpenEdit = async (item: Consignment) => {
        try {
            setLoading(true);
            const fullItem = await api_consignment('GET', {}, item.id);
            // The list row has no products, so opening the form from it would
            // show a bill with an empty item row as if that were the truth.
            if (!fullItem) throw new Error('empty consignment');
            setEditingItem(fullItem);
            setIsCreateModalOpen(true);
        } catch (e) {
            message.error('โหลดข้อมูลรอบฝากขายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
        } finally {
            setLoading(false);
        }
    };

    // Open Check: fetch single consignment details with items
    const handleOpenCheck = async (item: Consignment) => {
        try {
            setLoading(true);
            const fullItem = await api_consignment('GET', {}, item.id);
            // Same rule as edit: the list row has no products, so it must
            // never stand in for the bill.
            if (!fullItem) throw new Error('empty consignment');
            setCheckingItem(fullItem);
            setIsCheckModalOpen(true);
        } catch (e) {
            message.error('โหลดข้อมูลรอบฝากขายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
        } finally {
            setLoading(false);
        }
    };

    // Open Settle: fetch single consignment details with items
    const handleOpenSettle = async (item: Consignment) => {
        try {
            setLoading(true);
            const fullItem = await api_consignment('GET', {}, item.id);
            // Same rule as edit: the list row has no products, so it must
            // never stand in for the bill.
            if (!fullItem) throw new Error('empty consignment');
            setSettlingItem(fullItem);
            setIsSettleModalOpen(true);
        } catch (e) {
            message.error('โหลดข้อมูลรอบฝากขายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
        } finally {
            setLoading(false);
        }
    };

    const handleExpand = async (expanded: boolean, item: Consignment) => {
        if (!expanded) {
            setExpandedRowKeys(prev => prev.filter(k => k !== item.id));
            return;
        }
        setExpandedRowKeys(prev => Array.from(new Set([...prev, item.id])));
        try {
            const fullItem = await api_consignment('GET', {}, item.id);
            if (!fullItem) return;
            setConsignments(current => current.map(round => round.id === item.id ? { ...round, ...fullItem } : round));
        } catch (e) {
            message.error('โหลดรายการสินค้าในรอบฝากขายไม่สำเร็จ');
        }
    };

    // Save Create / Edit
    const handleSaveConsignment = async (values: any) => {
        if (editingItem) {
            await api_consignment('UPDATE', values, editingItem.id);
            message.success('แก้ไขข้อมูลรอบการฝากขายเรียบร้อย');
        } else {
            await api_consignment('INSERT', values);
            message.success('สร้างรอบการฝากขายใหม่เรียบร้อย');
        }
        loadConsignments();
    };

    // Save Stock Check -- V2: one counted remaining PER PRODUCT (aggregate
    // across every active Batch in the current Period), never per-Batch.
    // FIFO attribution across Batches is entirely Backend-authoritative.
    const handleSaveCheck = async (
        consignmentId: number,
        countItems: { product_id: number; latest_actual_remaining: number }[],
        note?: string,
        expectedPeriodId?: number
    ) => {
        await api_consignment('UPDATE_REMAINING', buildStockCheckSaveBody(countItems, note, expectedPeriodId), consignmentId);
        message.success('บันทึกการตรวจนับสต๊อกและอัปเดตยอดเรียบร้อย');
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, consignmentId]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);
    };

    // Backend-authoritative Stock Check preview (STOCK_CHECK_PREVIEW) --
    // read-only, zero side effects, uses the exact same FIFO/correction
    // engine UPDATE_REMAINING uses to persist. Never computed on the client.
    const handlePreviewCheck = async (
        consignmentId: number,
        payload: ConsignmentStockCheckPreviewRequest
    ): Promise<ConsignmentStockCheckPreviewResult> => {
        return api_consignment('STOCK_CHECK_PREVIEW', payload, consignmentId);
    };

    // "ปิดรอบบิล" (CLOSE_PERIOD) -- settles outstanding liability, closes
    // the current Period, carries unsold stock forward, opens the next
    // Period. Distinct from FINAL_CLOSE, which ends the whole Consignment.
    const handleClosePeriod = async (payload: ClosePeriodPayload): Promise<ConsignmentClosePeriodResult> => {
        if (!closePeriodRound) throw new Error('ไม่พบรอบการฝากขายที่ต้องการปิดรอบบิล');
        const round = closePeriodRound;
        const result: ConsignmentClosePeriodResult = await api_consignment('CLOSE_PERIOD', payload, round.id);
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, round.id]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);

        // Auto-open "ฝากเพิ่มรอบใหม่" right after a successful Close Period.
        // Fetch fresh full state directly (rather than relying on
        // loadConsignments' own async setState) so the Carry Forward rows
        // shown are exactly what CLOSE_PERIOD just persisted into the new
        // Period -- never the old/closed Period's data.
        try {
            const fullRound = await api_consignment('GET', {}, round.id);
            const carryForwardItems: ConsignmentItem[] = ((fullRound?.items || []) as ConsignmentItem[]).filter(
                it => it.period_id === result.new_period.id && it.source_type === 'CARRY_FORWARD'
            );
            setNewPeriodAddStockContext({
                consignmentId: round.id,
                documentNo: round.document_no,
                vendorName: round.vendor_name,
                newPeriod: result.new_period,
                carryForwardItems,
                mode: 'AFTER_CLOSE_PERIOD',
                activeProductIds: Array.from(new Set(carryForwardItems.map(it => it.product_id)))
            });
            setIsNewPeriodAddStockModalOpen(true);
        } catch (e) {
            console.error('Failed to load new Period state for the auto-opened Add Stock modal', e);
            // CLOSE_PERIOD itself already succeeded -- this optional
            // follow-up failing to open must never be reported as a Close
            // Period failure.
        }

        return result;
    };

    // "ฝากเพิ่มรอบใหม่" -- ADD_STOCK_BULK, called only after CLOSE_PERIOD has
    // already succeeded. Atomic: every requested line is a NEW Batch in the
    // just-opened Period, or none are (Backend-guaranteed).
    const handleSaveNewPeriodBulk = async (payload: ConsignmentAddStockBulkPayload): Promise<ConsignmentAddStockBulkResult> => {
        if (!newPeriodAddStockContext) throw new Error('ไม่พบข้อมูลรอบบิลใหม่ที่ต้องการฝากเพิ่ม');
        const consignmentId = newPeriodAddStockContext.consignmentId;
        const result: ConsignmentAddStockBulkResult = await api_consignment('ADD_STOCK_BULK', payload, consignmentId);
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, consignmentId]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);
        return result;
    };

    // Reload one bill, keeping it (and every other open bill) expanded.
    const refreshRound = async (roundId: number) => {
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, roundId]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);
    };

    // "+ เพิ่มสินค้าใหม่ในรอบนี้": a Product the shop has only just been
    // given, added to the Period that is open NOW -- any time, not only
    // right after "ปิดรอบบิล". Reads the bill fresh so the Period and the
    // Products already in it are what Backend holds, not what this screen
    // loaded earlier. Saved by the same ADD_STOCK_BULK as after a close.
    const handleOpenAddNewProducts = async (round: Consignment) => {
        try {
            const context = buildAddNewProductsContext(round, await api_consignment('GET', {}, round.id));
            if (!context) {
                message.error('บิลนี้ไม่มีรอบบิลที่เปิดอยู่ กรุณารีเฟรชข้อมูล');
                return;
            }
            setNewPeriodAddStockContext(context);
            setIsNewPeriodAddStockModalOpen(true);
        } catch (e) {
            message.error('โหลดข้อมูลรอบฝากขายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
        }
    };

    const handleEditBatch = async (payload: ConsignmentEditBatchPayload) => {
        if (!editBatchTarget) throw new Error('ไม่พบล็อตที่ต้องการแก้ไข');
        const roundId = editBatchTarget.round.id;
        const result = await api_consignment('EDIT_BATCH', payload, roundId);
        await refreshRound(roundId);
        return result;
    };

    const handleVoidBatch = async (payload: ConsignmentVoidBatchPayload) => {
        if (!voidBatchTarget) throw new Error('ไม่พบล็อตที่ต้องการยกเลิก');
        const roundId = voidBatchTarget.round.id;
        const result = await api_consignment('VOID_BATCH', payload, roundId);
        await refreshRound(roundId);
        return result;
    };

    // Pay only newly sold, previously unpaid quantities. The round remains ACTIVE.
    const handleSettle = async (consignmentId: number, note?: string) => {
        await api_consignment('PERIODIC_SETTLEMENT', { note }, consignmentId);
        message.success('บันทึกการจ่ายรอบนี้เรียบร้อย รอบฝากขายยังเปิดอยู่');
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, consignmentId]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);
    };

    const handleAddStock = async (payload: any) => {
        const { roundId } = resolveAddStockRequestContext(addStockRound, addStockItem, payload);
        const result = await api_consignment('ADD_STOCK', payload, roundId);
        if (result?.created_new_batch) {
            message.success('ฝากเพิ่มเรียบร้อย และสร้างล็อตใหม่เพื่อแยกเงื่อนไขราคา/ต้นทุน');
        } else {
            message.success('ฝากเพิ่มในล็อตเดิมเรียบร้อย');
        }
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, roundId]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);
        return result;
    };

    const handleReturnToOwner = async (payload: ReturnToOwnerPayload) => {
        if (!returnRound || !returnItem) throw new Error('ไม่พบรายการสินค้าที่ต้องการคืน');
        if (returnItem.consignment_id !== undefined && returnItem.consignment_id !== returnRound.id) {
            throw new Error('รอบการฝากขายของสินค้าไม่ตรงกับรอบที่เลือก กรุณาโหลดข้อมูลใหม่');
        }
        await api_consignment('RETURN_TO_OWNER', payload, returnRound.id);
        message.success('บันทึกการคืนสินค้าให้เจ้าของเรียบร้อย โดยไม่เพิ่มยอดขายหรือยอดจ่าย');
        const keysToPreserve = Array.from(new Set([...expandedRowKeys, returnRound.id]));
        setExpandedRowKeys(keysToPreserve);
        await loadConsignments(keysToPreserve);
    };

    const handleCloseItem = async (round: Consignment, item: ConsignmentItem) => {
        if (!item.id) return;
        try {
            await api_consignment('CLOSE_ITEM', { consignment_item_id: item.id }, round.id);
            message.success('ปิดรายการสินค้าเรียบร้อย');
            const keysToPreserve = Array.from(new Set([...expandedRowKeys, round.id]));
            setExpandedRowKeys(keysToPreserve);
            await loadConsignments(keysToPreserve);
        } catch (error: any) {
            message.error(error?.message || 'ปิดรายการสินค้าไม่สำเร็จ');
        }
    };

    const handleFinalClose = async (round: Consignment) => {
        try {
            await api_consignment('FINAL_CLOSE', {}, round.id);
            message.success('ปิดรอบฝากขายเรียบร้อย');
            await loadConsignments();
        } catch (error: any) {
            message.error(error?.message || 'ยังปิดรอบฝากขายไม่ได้');
        }
    };

    // Delete Consignment
    const handleDelete = async (id: number) => {
        try {
            await api_consignment('DELETE', {}, id);
            message.success('ลบรายการรอบการฝากขายเรียบร้อย');
            loadConsignments();
        } catch (e) {
            message.error(deleteFailureMessage(e, 'ลบรายการไม่สำเร็จ'));
        }
    };

    const renderStatusTag = (status: ConsignmentStatus) => {
        switch (status) {
            case 'ACTIVE':
                return <Tag className="consignment-tag active">🟢 ฝากขายอยู่</Tag>;
            case 'PENDING_SETTLEMENT':
                return <Tag className="consignment-tag pending">🟡 รอเคลียร์ยอด</Tag>;
            case 'SETTLED':
                return <Tag className="consignment-tag settled">🔵 ปิดยอดเดิม</Tag>;
            case 'RETURNED':
                return <Tag className="consignment-tag returned">↩️ คืนสินค้าแล้ว</Tag>;
            case 'CLOSED':
                return <Tag className="consignment-tag closed">⚪ ปิดรอบแล้ว</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const expandedRowRender = (record: Consignment) => {
        const currentPeriod = record.current_period;
        const currentPeriodBatches = (record.items || []).filter(it => !currentPeriod || it.period_id === currentPeriod.id);

        // Product-level aggregate summary -- the underlying Batch rows
        // remain fully visible in the table below; this is a display-only
        // rollup, never a persisted/merged item. Plain computation (NOT
        // useMemo): expandedRowRender is a plain callback invoked once per
        // expanded row, not a React component, so calling hooks here would
        // break the Rules of Hooks (hook call count would vary with the
        // number of expanded rows). Logic itself lives in the exported
        // buildProductSummary() so it can be unit-tested directly.
        const productSummary = buildProductSummary(record.items || [], currentPeriod);

        // Compact main columns only -- secondary/status fields (item status,
        // returned qty, selling price, unpaid payable, shop profit) move into
        // the "รายละเอียด" popover per row instead of widening the flat table.
        const itemColumns = [
            {
                title: 'งวด',
                dataIndex: 'period_id',
                key: 'period_id',
                width: 58,
                align: 'center' as const,
                render: (periodId: number, r: ConsignmentItem) => {
                    const p = (record.periods || []).find(pp => pp.id === (periodId ?? r.period_id));
                    return p ? <Tag color={p.status === 'OPEN' ? 'green' : 'default'}>#{p.period_no}</Tag> : '-';
                }
            },
            {
                title: 'ล็อตที่',
                dataIndex: 'batch_no',
                key: 'batch_no',
                width: 58,
                align: 'center' as const,
                render: (val: number) => <Tag>#{val || 1}</Tag>
            },
            {
                title: 'สินค้า',
                dataIndex: 'product_name',
                key: 'product_name',
                width: 160,
                render: (_: any, r: ConsignmentItem) => (
                    <div>
                        <span style={{ fontWeight: 700 }}>{r.product_name}</span>
                        {r.product_code && <span className="consignment-cell-sub" style={{ marginLeft: '6px' }}>({r.product_code})</span>}
                    </div>
                )
            },
            {
                title: 'ที่มา',
                dataIndex: 'source_type',
                key: 'source_type',
                width: 100,
                render: (_: string, r: ConsignmentItem) => <ConsignmentLotSource item={r} />
            },
            {
                title: 'รับฝาก',
                dataIndex: 'quantity_received',
                key: 'quantity_received',
                width: 75,
                align: 'right' as const,
                render: (val: number, r: ConsignmentItem) => `${val} ${r.product_unit_name || ''}`
            },
            {
                title: 'คงเหลือจริง',
                dataIndex: 'latest_actual_remaining',
                key: 'latest_actual_remaining',
                width: 85,
                align: 'right' as const,
                render: (val: number, r: ConsignmentItem) => `${val} ${r.product_unit_name || ''}`
            },
            {
                title: 'ขายได้',
                dataIndex: 'quantity_sold',
                key: 'quantity_sold',
                width: 75,
                align: 'right' as const,
                render: (val: number) => (
                    <span className="consignment-cell-sold" style={{ color: '#60A5FA', fontWeight: 700 }}>{val}</span>
                )
            },
            {
                title: 'จ่ายคืน/หน่วย',
                dataIndex: 'owner_cost_per_unit',
                key: 'owner_cost_per_unit',
                width: 95,
                align: 'right' as const,
                render: (val: number) => `฿${Number(val || 0).toFixed(2)}`
            },
            {
                title: 'ยอดขาย',
                dataIndex: 'sales_amount',
                key: 'sales_amount',
                width: 100,
                align: 'right' as const,
                render: (val: number) => (
                    <span className="consignment-cell-sales">
                        ฿{Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                )
            },
            {
                title: 'จ่ายแล้ว',
                dataIndex: 'quantity_settled',
                key: 'quantity_settled',
                width: 70,
                align: 'right' as const,
                render: (val: number) => Number(val || 0)
            },
            {
                title: 'รายละเอียด',
                key: 'more_details',
                width: 118,
                align: 'center' as const,
                render: (_: any, item: ConsignmentItem) => {
                    const itemStatus = item.item_status || 'ACTIVE';
                    const detailContent = (
                        <div className="consignment-batch-detail-popover">
                            <div className="consignment-batch-detail-row">
                                <span className="consignment-batch-detail-label">สถานะสินค้า</span>
                                {itemStatus === 'VOIDED'
                                    ? <Tag className="consignment-tag-voided">ยกเลิกแล้ว</Tag>
                                    : itemStatus === 'CLOSED'
                                        ? <Tag>ปิดแล้ว</Tag>
                                        : itemStatus === 'CARRIED_FORWARD'
                                            ? <Tag color="cyan">ยกยอดแล้ว</Tag>
                                            : <Tag color="green">ใช้งาน</Tag>}
                            </div>
                            <div className="consignment-batch-detail-row">
                                <span className="consignment-batch-detail-label">คืนเจ้าของ</span>
                                <span>{Number(item.quantity_returned || 0)} {item.product_unit_name || ''}</span>
                            </div>
                            <div className="consignment-batch-detail-row">
                                <span className="consignment-batch-detail-label">ราคาขาย</span>
                                <span>฿{Number(item.selling_price_per_unit || 0).toFixed(2)}</span>
                            </div>
                            <div className="consignment-batch-detail-row">
                                <span className="consignment-batch-detail-label">ค้างจ่ายผู้ฝาก</span>
                                <span className="consignment-cell-payable">
                                    ฿{Number(item.unpaid_owner_payable ?? item.owner_payable ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                            <div className="consignment-batch-detail-row">
                                <span className="consignment-batch-detail-label">กำไรร้าน</span>
                                <span className="consignment-cell-profit">
                                    ฿{Number(item.shop_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>
                    );
                    return (
                        <ConsignmentBatchActions
                            item={item}
                            detail={detailContent}
                            onEdit={() => setEditBatchTarget({ round: record, item })}
                            onVoid={() => setVoidBatchTarget({ round: record, item })}
                        />
                    );
                }
            },
            {
                title: 'จัดการสินค้า',
                key: 'item_actions',
                className: 'consignment-add-stock-column',
                width: 190,
                align: 'center' as const,
                fixed: 'right' as const,
                render: (_: any, item: ConsignmentItem) => (
                    <ConsignmentLotManageActions
                        roundStatus={record.status}
                        item={item}
                        onReturn={() => {
                            setReturnRound(record);
                            setReturnItem(item);
                        }}
                        onCloseItem={() => handleCloseItem(record, item)}
                    />
                )
            }
        ];

        return (
            <div className="consignment-expanded-inner">
                <ConsignmentPeriodBanner
                    record={record}
                    onAddNewProducts={() => handleOpenAddNewProducts(record)}
                    onOpenPeriodHistory={() => {
                        setPeriodHistoryItem(record);
                        setIsPeriodHistoryModalOpen(true);
                    }}
                />

                {productSummary.length > 0 && (
                    <div className="consignment-product-summary" style={{ marginBottom: 12 }}>
                        <div className="consignment-expanded-title" style={{ marginBottom: 6 }}>
                            <span aria-hidden="true">📊</span>
                            <span>สรุปตามสินค้า (งวดปัจจุบัน)</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(productSummary.length, 3)}, 1fr)`, gap: 8 }}>
                            {productSummary.map(p => (
                                <div
                                    key={p.product_id}
                                    className="consignment-product-summary-card"
                                    data-testid={`product-summary-${p.product_id}`}
                                    style={{ border: '1px solid #E2E8F0', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.product_name} {p.batchCount > 1 && <Tag>{p.batchCount} ล็อต</Tag>}</div>
                                        <div style={{ fontSize: 12, color: '#64748B', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                            <span>รวมรับ {p.received}</span>
                                            <span>รวมคงเหลือ {p.remaining}</span>
                                            <span>รวมขาย {p.sold}</span>
                                            <span>รวมจ่ายแล้ว {p.settled}</span>
                                        </div>
                                    </div>
                                    {/* Single product-level "ฝากเพิ่ม" for this Product's current OPEN
                                        Period -- replaces the old per-Batch-row repeated button below.
                                        Only shown when there's actually an open Period AND an ACTIVE
                                        Batch to reference (same rule ConsignmentAddStockAction itself
                                        already enforces, kept visible here instead of hidden inside it
                                        so a Product with no active reference simply shows no action). */}
                                    {record.status === 'ACTIVE' && currentPeriod && p.referenceBatch && (
                                        <div>
                                            <ConsignmentAddStockAction
                                                roundStatus={record.status}
                                                item={p.referenceBatch}
                                                onClick={() => {
                                                    setAddStockRound(record);
                                                    setAddStockItem(p.referenceBatch!);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="consignment-expanded-title">
                    <span aria-hidden="true">📦</span>
                    <span>
                        รายการล็อตทั้งหมดในรอบฝากขายนี้ ({record.items?.length || 0} รายการ
                        {(record.voided_items?.length || 0) > 0 ? ` · ยกเลิกแล้ว ${record.voided_items!.length}` : ''})
                    </span>
                    <small>ทุกล็อตแสดงแยกกันเสมอ ไม่มีการรวมยอดหรือเฉลี่ยราคาข้ามล็อต · เรียงงวดที่เปิดอยู่และล็อตล่าสุดไว้บนสุด</small>
                </div>
                <Table
                    columns={itemColumns}
                    dataSource={buildLotRows(record).map((it, idx) => ({ ...it, key: it.id || idx }))}
                    rowClassName={(r: ConsignmentItem) => isVoidedItem(r) ? 'consignment-row-voided' : ''}
                    pagination={false}
                    size="small"
                    bordered
                    scroll={{ x: 1230 }}
                />
            </div>
        );
    };

    const columns = [
        {
            title: 'รหัสเอกสาร',
            dataIndex: 'document_no',
            key: 'document_no',
            width: 120,
            render: (text: string) => <span className="consignment-cell-doc">{text}</span>
        },
        {
            title: 'วันที่รับฝาก',
            dataIndex: 'consignment_date',
            key: 'consignment_date',
            width: 110,
            render: (text: string) => <span className="consignment-cell-sub">{text || '-'}</span>
        },
        {
            title: 'ผู้ฝากขาย (Consignor)',
            key: 'vendor',
            width: 180,
            render: (_: any, r: Consignment) => (
                <div>
                    <div className="consignment-cell-name">{r.vendor_name || '-'}</div>
                    {r.vendor_tel && <div className="consignment-cell-sub">📞 {r.vendor_tel}</div>}
                </div>
            )
        },
        {
            title: 'จำนวนรายการ',
            key: 'item_count',
            width: 100,
            align: 'center' as const,
            render: (_: any, r: Consignment) => (
                <Tag color="blue" style={{ fontWeight: 600 }}>
                    {r.item_count ?? r.items?.length ?? 0} รายการ
                </Tag>
            )
        },
        {
            title: 'รับฝากรวม',
            dataIndex: 'total_received_qty',
            key: 'total_received_qty',
            width: 90,
            align: 'right' as const,
            render: (val: number) => (
                <span className="consignment-cell-qty">{val} ชิ้น</span>
            )
        },
        {
            title: 'มูลค่าสินค้ารวม',
            dataIndex: 'total_received_retail_value',
            key: 'total_received_retail_value',
            width: 120,
            align: 'right' as const,
            render: (val: Consignment['total_received_retail_value']) => {
                const value = val === null || val === undefined || val === '' ? NaN : Number(val);
                return (
                    <Tooltip title="ราคาขาย × จำนวนที่รับเข้าร้านจริง (รับฝากครั้งแรก + เพิ่มสต๊อก) ไม่นับของที่ยกมาจากรอบก่อนซ้ำ">
                        <span className="consignment-cell-sales" data-testid="consignment-received-retail-value">
                            {Number.isFinite(value)
                                ? `฿${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : '-'}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            title: 'ขายได้รวม',
            dataIndex: 'total_sold_qty',
            key: 'total_sold_qty',
            width: 90,
            align: 'right' as const,
            render: (val: number) => (
                <span className="consignment-cell-sold" style={{ color: '#60A5FA', fontWeight: 700 }}>
                    {val}
                </span>
            )
        },
        {
            title: 'ยอดขายรวม',
            dataIndex: 'total_sales_amount',
            key: 'total_sales_amount',
            width: 110,
            align: 'right' as const,
            render: (val: number) => (
                <span className="consignment-cell-sales">
                    ฿{Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
            )
        },
        {
            title: 'ค้างจ่ายผู้ฝาก',
            dataIndex: 'total_unpaid_owner_payable',
            key: 'total_unpaid_owner_payable',
            width: 120,
            align: 'right' as const,
            render: (val: number, round: Consignment) => (
                <span className="consignment-cell-payable" style={{ color: '#F87171', fontWeight: 800 }}>
                    ฿{Number(val ?? round.total_owner_payable ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
            )
        },
        {
            title: 'กำไรร้าน',
            dataIndex: 'total_shop_profit',
            key: 'total_shop_profit',
            width: 110,
            align: 'right' as const,
            render: (val: number) => (
                <span className="consignment-cell-profit" style={{ color: '#34D399', fontWeight: 800 }}>
                    ฿{Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
            )
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            align: 'left' as const,
            render: (status: ConsignmentStatus) => renderStatusTag(status)
        },
        {
            title: 'ตรวจนับล่าสุด',
            dataIndex: 'last_checked_at',
            key: 'last_checked_at',
            width: 140,
            render: (val: string) => <span className="consignment-cell-sub">{val || '-'}</span>
        },
        {
            title: 'การดำเนินการ',
            key: 'actions',
            width: 320,
            fixed: 'right' as const,
            render: (_: any, r: Consignment) => (
                <div className="consignment-actions-stack">
                    {/* แถวที่ 1: ปุ่มปฏิบัติการหลัก (นับ / จ่ายรอบนี้) */}
                    <div className="consignment-actions-row">
                        {r.status === 'ACTIVE' && (
                            <Tooltip title="ตรวจนับสต๊อกคงเหลือจริง">
                                <button
                                    type="button"
                                    onClick={() => handleOpenCheck(r)}
                                    data-testid={`btn-check-${r.id}`}
                                    className="consignment-btn-action check"
                                >
                                    <FontAwesomeIcon icon="clipboard-check" />
                                    <span>นับ</span>
                                </button>
                            </Tooltip>
                        )}

                        {r.status === 'ACTIVE'
                            && Number(r.total_sold_qty || 0) - Number(r.total_settled_qty || 0) > 0
                            && (
                            <Tooltip title="จ่ายเฉพาะยอดขายใหม่ที่ยังไม่เคยจ่าย">
                                <button
                                    type="button"
                                    onClick={() => handleOpenSettle(r)}
                                    data-testid={`btn-settle-${r.id}`}
                                    className="consignment-btn-action settle"
                                >
                                    <FontAwesomeIcon icon="money-bill-wave" />
                                    <span>จ่ายรอบนี้</span>
                                </button>
                            </Tooltip>
                        )}
                    </div>

                    {/* แถวที่ 2: ปุ่มปิดรอบ (ปิดรอบบิล / ปิดรายการฝากขายทั้งหมด) */}
                    <div className="consignment-actions-row">
                        {r.status === 'ACTIVE' && r.current_period && (
                            <Tooltip title="ชำระยอดค้าง ปิดรอบบิลปัจจุบัน และยกสินค้าคงเหลือไปเปิดรอบบิลใหม่">
                                <Button
                                    size="small"
                                    className="consignment-v15-action consignment-v15-action-close-period"
                                    onClick={() => {
                                        setClosePeriodRound(r);
                                        setIsClosePeriodModalOpen(true);
                                    }}
                                    data-testid={`btn-close-period-${r.id}`}
                                >
                                    🔒 ปิดรอบบิล
                                </Button>
                            </Tooltip>
                        )}

                        {r.status === 'ACTIVE' && (
                            <Popconfirm
                                overlayClassName="consignment-v15-popconfirm"
                                title="ปิดรายการฝากขายทั้งหมดนี้?"
                                description="ทำได้เมื่อสินค้าทุกตัวคงเหลือ 0 ไม่มียอดค้างจ่าย และปิดรายการสินค้าแล้วทุกตัว"
                                onConfirm={() => handleFinalClose(r)}
                                okText="ปิดรายการฝากขายทั้งหมด"
                                cancelText="ยกเลิก"
                            >
                                <Button
                                    size="small"
                                    className="consignment-v15-action consignment-v15-action-final"
                                    data-testid={`btn-final-close-${r.id}`}
                                >
                                    ปิดรายการฝากขายทั้งหมด
                                </Button>
                            </Popconfirm>
                        )}
                    </div>

                    {/* แถวที่ 3: ไอคอนประวัติ/แก้ไข/ลบ */}
                    <div className="table-action-group">
                        <Tooltip title="ดูประวัติการตรวจนับ">
                            <button
                                type="button"
                                onClick={() => {
                                    setHistoryItem(r);
                                    setIsHistoryModalOpen(true);
                                }}
                                data-testid={`btn-history-${r.id}`}
                                className="table-action-button"
                            >
                                <FontAwesomeIcon icon="history" />
                            </button>
                        </Tooltip>

                        {r.status !== 'SETTLED' && (
                            <Tooltip title="แก้ไขข้อมูล">
                                <button
                                    type="button"
                                    onClick={() => handleOpenEdit(r)}
                                    data-testid={`btn-edit-${r.id}`}
                                    className="table-action-button edit"
                                >
                                    <FontAwesomeIcon icon="pen-to-square" />
                                </button>
                            </Tooltip>
                        )}

                        {r.status !== 'SETTLED' && (
                            <Popconfirm
                                title="ยืนยันการลบรอบการฝากขาย?"
                                description="การลบจะยกเลิกข้อมูลรอบการฝากขายนี้"
                                onConfirm={() => handleDelete(r.id)}
                                okText="ลบรายการ"
                                cancelText="ยกเลิก"
                                okButtonProps={{ danger: true }}
                            >
                                <Tooltip title="ลบรายการ">
                                    <button
                                        type="button"
                                        data-testid={`btn-delete-${r.id}`}
                                        className="table-action-button delete"
                                    >
                                        <FontAwesomeIcon icon="trash-can" />
                                    </button>
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="consignment-container">
            {/* Header */}
            <div className="consignment-header-row">
                <div className="consignment-header-title">
                    <h2>📦 จัดการสินค้าฝากขาย (Multi-item Consignment V1.5)</h2>
                    <p>ตรวจนับ ฝากเพิ่ม จ่ายเป็นรอบ คืนสินค้า และปิดรอบโดยมี Movement Ledger ครบถ้วน</p>
                </div>
                <div className="consignment-header-actions">
                    <Button
                        className="consignment-btn-count-profit"
                        icon={<FontAwesomeIcon icon="chart-line" style={{ marginRight: '6px' }} />}
                        onClick={() => setIsCountProfitOpen(true)}
                        data-testid="btn-count-profit"
                    >
                        กำไรจากการตรวจนับ
                    </Button>
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon="plus" style={{ marginRight: '6px' }} />}
                        onClick={() => {
                            setEditingItem(null);
                            setIsCreateModalOpen(true);
                        }}
                        data-testid="btn-create-consignment"
                        style={{
                            background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            fontWeight: 700,
                            borderRadius: '8px',
                            height: '40px'
                        }}
                    >
                        เพิ่มรายการฝากขาย
                    </Button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="consignment-stats-grid">
                <div className="consignment-stat-card">
                    <div className="stat-label">รอบการฝากขายทั้งหมด</div>
                    <div className="stat-value">{stats.totalRounds} <span className="stat-unit">รอบ</span></div>
                    <div className="stat-sub">รับฝากรวม {stats.totalReceived} ชิ้น</div>
                </div>

                <div className="consignment-stat-card">
                    <div className="stat-label">ยอดขายรวมทั้งหมด</div>
                    <div className="stat-value sales">
                        ฿{stats.totalSales.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="stat-sub">ขายได้ {stats.totalSold} ชิ้น</div>
                </div>

                <div className="consignment-stat-card payable">
                    <div className="stat-label">ยอดค้างจ่ายผู้ฝากขาย</div>
                    <div className="stat-value payable">
                        ฿{stats.totalPayable.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="stat-sub">ต้นทุนที่ต้องคืนเจ้าของสินค้า</div>
                </div>

                <div className="consignment-stat-card profit">
                    <div className="stat-label">กำไรส่วนแบ่งของร้านค้า</div>
                    <div className="stat-value profit">
                        ฿{stats.totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="stat-sub">รายได้สุทธิของร้านค้า</div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="consignment-filter-bar">
                <div className="search-box">
                    <Input
                        placeholder="🔍 ค้นหารหัสเอกสาร, ชื่อผู้ฝากขาย, สินค้า..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onPressEnter={handleSearch}
                        allowClear
                        data-testid="input-search-consignment"
                    />
                </div>
                <div className="filter-group">
                    <Select
                        value={statusFilter}
                        onChange={(val) => setStatusFilter(val)}
                        style={{ width: 160 }}
                        options={[
                            { value: 'ALL', label: 'สถานะทั้งหมด' },
                            { value: 'ACTIVE', label: '🟢 ฝากขายอยู่' },
                            { value: 'PENDING_SETTLEMENT', label: '🟡 รอเคลียร์ยอด' },
                            { value: 'SETTLED', label: '🔵 ปิดยอดเดิม' },
                            { value: 'RETURNED', label: '↩️ คืนสินค้า' },
                            { value: 'CLOSED', label: '⚪ ปิดรอบแล้ว' }
                        ]}
                    />
                    <Button onClick={() => loadConsignments()} icon={<FontAwesomeIcon icon="rotate-right" />}>
                        รีเฟรช
                    </Button>
                </div>
            </div>

            {/* Consignments Table with Expandable Inner Rows */}
            <div className="consignment-table-wrapper" ref={tableRef}>
                <Table
                    className="consignment-main-table"
                    columns={columns}
                    dataSource={consignments.map(c => ({ ...c, key: c.id }))}
                    rowKey="id"
                    loading={loading}
                    bordered
                    expandable={{
                        columnTitle: 'รายการสินค้า',
                        columnWidth: 72,
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as React.Key[]),
                        expandedRowRender,
                        rowExpandable: record => record.status === 'ACTIVE' || Number(record.item_count || record.items?.length || 0) > 0,
                        onExpand: handleExpand
                    }}
                    pagination={pagination}
                    scroll={{ x: 1200, y: scrollY }}
                />
            </div>

            {/* Modals */}
            <ConsignmentModal
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSave={handleSaveConsignment}
                editItem={editingItem}
            />

            <ConsignmentCheckModal
                open={isCheckModalOpen}
                item={checkingItem}
                onClose={() => setIsCheckModalOpen(false)}
                onSave={handleSaveCheck}
                onPreview={handlePreviewCheck}
                onStale={() => checkingItem && refreshRound(checkingItem.id)}
            />

            <ConsignmentSettleModal
                open={isSettleModalOpen}
                item={settlingItem}
                onClose={() => setIsSettleModalOpen(false)}
                onSettle={handleSettle}
            />

            <ConsignmentHistoryModal
                open={isHistoryModalOpen}
                item={historyItem}
                onClose={() => setIsHistoryModalOpen(false)}
            />

            <ConsignmentAddStockModal
                open={!!addStockItem}
                item={addStockItem}
                onClose={() => {
                    setAddStockItem(null);
                    setAddStockRound(null);
                }}
                onAddStock={handleAddStock}
            />

            <ConsignmentReturnModal
                open={!!returnItem}
                item={returnItem}
                onClose={() => {
                    setReturnItem(null);
                    setReturnRound(null);
                }}
                onReturn={handleReturnToOwner}
            />

            <ConsignmentClosePeriodModal
                open={isClosePeriodModalOpen}
                round={closePeriodRound}
                onClose={() => {
                    setIsClosePeriodModalOpen(false);
                    setClosePeriodRound(null);
                }}
                onClosePeriod={handleClosePeriod}
            />

            <ConsignmentPeriodHistoryModal
                open={isPeriodHistoryModalOpen}
                item={periodHistoryItem}
                onClose={() => {
                    setIsPeriodHistoryModalOpen(false);
                    setPeriodHistoryItem(null);
                }}
            />

            <ConsignmentNewPeriodAddStockModal
                open={isNewPeriodAddStockModalOpen}
                context={newPeriodAddStockContext}
                onClose={() => {
                    setIsNewPeriodAddStockModalOpen(false);
                    setNewPeriodAddStockContext(null);
                }}
                onSaveBulk={handleSaveNewPeriodBulk}
                onStale={() => newPeriodAddStockContext && refreshRound(newPeriodAddStockContext.consignmentId)}
            />

            <ConsignmentBatchEditModal
                open={!!editBatchTarget}
                item={editBatchTarget?.item || null}
                periodNo={editBatchTarget?.round.current_period?.period_no}
                expectedPeriodId={editBatchTarget?.round.current_period?.id}
                onClose={() => setEditBatchTarget(null)}
                onSave={handleEditBatch}
                onStale={() => editBatchTarget && refreshRound(editBatchTarget.round.id)}
            />

            <ConsignmentBatchVoidModal
                open={!!voidBatchTarget}
                item={voidBatchTarget?.item || null}
                expectedPeriodId={voidBatchTarget?.round.current_period?.id}
                onClose={() => setVoidBatchTarget(null)}
                onVoid={handleVoidBatch}
                onStale={() => voidBatchTarget && refreshRound(voidBatchTarget.round.id)}
            />

            {isCountProfitOpen && (
                <ConsignmentCountProfitModal open onClose={() => setIsCountProfitOpen(false)} />
            )}
        </div>
    );
};

export default ConsignmentPage;
